import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { DateTime } from "luxon";
import { toPng } from "html-to-image";
import { Menu, Transition } from "@headlessui/react";
import {
  CameraIcon,
  ChevronDownIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  ShoppingBagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import classNames from "../../../utils/classNames";
import openInNewTab from "../../../utils/openInNewTab";
import PriceFormat from "../../../utils/priceFormat";
import formatDateTime from "../../../utils/formatDateTime";
import { IInvoice } from "../../../models/invoice/IInvoice";
import { IPart, IPartCancelRequest, PartStatus } from "../../../models/Part";
import useInvoiceMutations from "../../../data/useInvoiceMutations";
import useLeadByVehicleId from "../../../data/useLeadByVehicleId";
import useSinglePartMutations from "../../../data/useSinglePartMutations";
import useVehicleImages from "../../../data/useVehicleImages";
import useVehicleImagesMutations from "../../../data/useVehicleImagesMutations";
import { useLabelGenerator } from "../../../hooks/useLabelGenerator";
import useNotification from "../../notifications/useNotifications";
import SelectInput from "../../shared/Inputs/SelectInput";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import CreditInvoiceModal from "../../invoices/CreditInvoiceModal";
import PartGradeBadge from "../../shared/PartGradeBadge";
import SendToQcModal from "../SendToQcModal";
import { ManageShippingModal } from "../shipping/ManageShippingModal";
import TrackingInfoModal from "../TrackingInfoModal";
import PinProtect, { PinProtectState } from "../../shared/PinProtect";
import UnlistedOfferPrice from "../../ebayListings/unlistedTable/UnlistedOfferPrice";
import EbayResearchButton from "./EbayResearchButton";
import StockStatusEditor from "./StockStatusEditor";
import StockImageModal, { StockLightboxImage } from "./StockImageModal";

interface StockHeaderProps {
  part: IPart;
  invoice: IInvoice | null | undefined;
}

export default function StockHeader({ part, invoice }: StockHeaderProps) {
  const { cancel } = useSinglePartMutations(part.id!);
  const { downloadInvoice } = useInvoiceMutations();
  const { lead } = useLeadByVehicleId("StockHeader");
  const { addNotification } = useNotification();
  const { generatePartLabel } = useLabelGenerator();
  const { vehicleImages } = useVehicleImages(part.vehicleId ?? "");
  const { downloadDoc: downloadVehicleDoc } = useVehicleImagesMutations();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [partImagesOpen, setPartImagesOpen] = useState(false);
  const [vehicleImageOpen, setVehicleImageOpen] = useState(false);

  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();

  const [labelHtml, setLabelHtml] = useState("");
  const labelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    generatePartLabel(part).then((html) => {
      setLabelHtml(html);
    });
  }, [part]);

  const cancelReasonOptions = [
    { id: "ADDRESS_ISSUES", name: "Address Issues" },
    { id: "BUYER_ASKED_CANCEL", name: "Buyer Requested Cancellation" },
    { id: "ORDER_UNPAID", name: "Order Unpaid" },
    { id: "OUT_OF_STOCK_OR_CANNOT_FULFILL", name: "Out of Stock or Cannot Fulfill" },
    { id: "UNKNOWN", name: "Unknown" },
  ];

  const { watch, register, setValue, getValues } = useForm<IPartCancelRequest>({
    defaultValues: {
      cancelReason: "ADDRESS_ISSUES",
      partId: part.id!,
    },
  });

  const partLightboxImages: StockLightboxImage[] = useMemo(() => {
    const list = Array.isArray(part.images) ? part.images : [];
    const mapped: StockLightboxImage[] = list
      .filter((img) => !!img?.url)
      .map((img, i) => ({
        url: img.url!,
        alt: `${part.title ?? "Part"} image ${i + 1}`,
      }));
    if (mapped.length === 0 && part.cdnThumbnail) {
      mapped.push({ url: part.cdnThumbnail, alt: part.title ?? "Part image" });
    }
    if (mapped.length === 0 && part.ebayThumbnail) {
      mapped.push({ url: part.ebayThumbnail, alt: part.title ?? "Part image" });
    }
    return mapped;
  }, [part]);

  const partThumbUrl = partLightboxImages[0]?.url ?? null;
  const vehicleStockImageUrl = part.vehicle?.vehicleImageUrl ?? null;

  const vehicleLabel = useMemo(
    () =>
      [part.vehicle?.combinedMake, part.vehicle?.combinedModel]
        .filter(Boolean)
        .join(" ") || "Vehicle image",
    [part.vehicle]
  );

  const donorImageDocs = useMemo(() => {
    const docs = vehicleImages.data;
    if (!Array.isArray(docs)) return [];
    return docs.filter(
      (d) =>
        !!d.thumbnail &&
        !(d.contentType && d.contentType.includes("video"))
    );
  }, [vehicleImages.data]);

  const vehicleLightboxImages: StockLightboxImage[] = useMemo(() => {
    const list: StockLightboxImage[] = donorImageDocs.map((doc, i) => ({
      id: doc.id,
      url: `data:${doc.contentType ?? "image/png"};base64,${doc.thumbnail}`,
      alt: doc.fileName ?? `${vehicleLabel} photo ${i + 1}`,
    }));
    if (list.length === 0 && vehicleStockImageUrl) {
      list.push({ url: vehicleStockImageUrl, alt: vehicleLabel });
    }
    return list;
  }, [donorImageDocs, vehicleStockImageUrl, vehicleLabel]);

  const vehicleThumbUrl = vehicleLightboxImages[0]?.url ?? vehicleStockImageUrl;

  const resolveDonorFullUrl = useCallback(
    (img: StockLightboxImage): Promise<string | null> =>
      new Promise((resolve) => {
        if (!img.id) {
          resolve(null);
          return;
        }
        downloadVehicleDoc.mutate(img.id, {
          onSuccess: (file: unknown) => {
            try {
              const url = window.URL.createObjectURL(file as Blob);
              resolve(url);
            } catch {
              resolve(null);
            }
          },
          onError: () => resolve(null),
        });
      }),
    [downloadVehicleDoc]
  );

  const daysSince = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const dt = DateTime.fromISO(iso);
    if (!dt.isValid) return null;
    const diff = Math.floor(DateTime.now().diff(dt, "days").days);
    return diff < 0 ? 0 : diff;
  };

  const stockStartIso = part.removedOn ?? part.created ?? null;
  const daysInStock = useMemo(() => daysSince(stockStartIso), [stockStartIso]);

  const firstOffer = part.offers?.[0];
  const listedIso = part.listedDate ?? firstOffer?.listingStarted ?? null;
  const daysListed = useMemo(() => daysSince(listedIso), [listedIso]);

  const isSold = part.partStatus === PartStatus.Sold || !!part.price;
  const soldPrice = isSold ? part.price ?? null : null;

  const getCancellable = () => {
    if (part.soldDate === null) return "No - Not Sold";
    if (part.shippedOn !== null) return "No - Already Shipped";
    if (new Date().getTime() - new Date(part.soldDate!).getTime() > 7 * 24 * 60 * 60 * 1000)
      return "Unlikely - Sold Too Long Ago";
    return "Yes";
  };

  const doCancel = () => {
    cancel.mutate(getValues(), {
      onSuccess: () => {
        setCancelOpen(false);
      },
    });
  };

  const handleGoToLead = () => {
    if (!part.vehicle?.id) return;
    lead.mutate(part.vehicle.id, {
      onSuccess: (response) => {
        if (response?.data?.leads && response.data.leads.length > 0) {
          openInNewTab(`/leads/${response.data.leads[0].id}`);
        } else {
          addNotification({
            variant: "error",
            primaryText: "Could not find associated lead",
          });
        }
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to lookup lead",
        });
      },
    });
  };

  const printPartLabel = async () => {
    const html = await generatePartLabel(part);
    const iframe = document?.getElementById("stock-header-print-frame") as
      | HTMLIFrameElement
      | null;
    const pri = iframe?.contentWindow;
    if (!pri) return;
    pri.document.open();
    pri.document.write(html);
    pri.document.close();
    pri.focus();
    setTimeout(() => {
      pri.print();
    }, 1);
  };

  const printPartLabelImage = useCallback(() => {
    if (labelRef.current === null) return;
    toPng(labelRef.current)
      .then((dataUrl) => {
        const iframe = document?.getElementById("stock-header-print-frame") as
          | HTMLIFrameElement
          | null;
        const pri = iframe?.contentWindow;
        if (!pri) return;
        pri.document.open();
        pri.document.write(`<img src=${dataUrl} />`);
        pri.document.close();
        pri.focus();
        setTimeout(() => {
          pri.print();
        }, 1);
      })
      .catch((error) => {
        console.error("Something went wrong!", error);
      });
  }, []);

  const hasInvoice = !!invoice;
  const hasEbayItem = !!part.ebayItemNumber;
  const hasOrder = !!part.orderId;
  const hasVehicle = !!part.vehicle?.id;
  const hasPartImages = partLightboxImages.length > 0;
  const hasVehicleImage = !!vehicleThumbUrl;
  const isShipped = part.shippedOn !== null;

  const vehicleYear = part.vehicle?.dvlaYearOfManufacture ?? null;
  const vehicleMake = part.vehicle?.combinedMake ?? null;
  const vehicleModel = part.vehicle?.combinedModel ?? null;
  const vehicleSummary = [vehicleYear, vehicleMake, vehicleModel]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Modal open={cancelOpen} setOpen={setCancelOpen} width="max-w-xl">
        <>
          <p className="text-xl font-semibold">Cancel Order</p>
          <div className="w-full flex flex-col gap-y-4 my-4">
            <p className="flex justify-between">
              <span>Title: </span>
              <span>{part.title}</span>
            </p>
            <p className="flex justify-between">
              <span>Tag: </span>
              <span>{part.tagNumber}</span>
            </p>
            <p className="flex justify-between">
              <span>Sold At: </span>
              <span>{formatDateTime(part.soldDate, DateTime.DATETIME_SHORT, "Not Sold")}</span>
            </p>
            <p className="flex justify-between">
              <span>Cancellation Acceptable: </span>
              <span>{getCancellable()}</span>
            </p>
            <SelectInput
              label="Reason for Cancellation"
              options={cancelReasonOptions}
              setValue={setValue}
              watch={watch}
              register={register}
              registerName={"cancelReason"}
            />
          </div>

          {confirmCancel ? (
            <div className="flex flex-row min-w-full gap-x-4">
              <ButtonInput
                label="Confirm"
                isSubmit={false}
                onClick={() => doCancel()}
                classes={"flex-grow"}
              />
              <ButtonInput
                label="Close"
                isSubmit={false}
                onClick={() => setConfirmCancel(false)}
                classes={"flex-grow"}
              />
            </div>
          ) : (
            <div className="flex flex-row min-w-full">
              <ButtonInput
                label="Cancel order"
                isSubmit={false}
                onClick={() => setConfirmCancel(true)}
                classes={"flex-grow"}
              />
            </div>
          )}
        </>
      </Modal>

      <SendToQcModal open={qcOpen} setOpen={setQcOpen} parts={[part]} />

      {invoice && (
        <CreditInvoiceModal
          open={creditModalOpen}
          setOpen={setCreditModalOpen}
          invoice={invoice}
        />
      )}

      <StockImageModal
        open={partImagesOpen}
        setOpen={setPartImagesOpen}
        images={partLightboxImages}
        title={part.title ?? "Part Images"}
      />

      <StockImageModal
        open={vehicleImageOpen}
        setOpen={setVehicleImageOpen}
        images={vehicleLightboxImages}
        title={vehicleSummary || "Vehicle"}
        resolveFullUrl={resolveDonorFullUrl}
      />

      <PinProtect
        open={pinModalOpen}
        setOpen={setPinModalOpen}
        pinState={pinState}
      />
      <TrackingInfoModal
        isOpen={trackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        customLoading={customLoading}
        setCustomLoading={setCustomLoading}
        part={part}
        setPinModalOpen={setPinModalOpen}
        setPinState={setPinState}
      />
      <Modal
        open={shippingModalOpen}
        setOpen={setShippingModalOpen}
        width="max-w-4xl mx-4"
      >
        <ManageShippingModal part={part} setOpen={setShippingModalOpen} />
      </Modal>

      <div
        ref={labelRef}
        className="pointer-events-none fixed -top-[10000px] left-0 w-[400px] opacity-0"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: labelHtml }}
      />
      <iframe id="stock-header-print-frame" className="h-0 w-0 absolute" title="Print frame" />

      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => hasPartImages && setPartImagesOpen(true)}
              disabled={!hasPartImages}
              aria-label={hasPartImages ? "View part images" : "No part images"}
              className={classNames(
                "relative flex-shrink-0 w-32 h-32 md:w-40 md:h-40 bg-gray-50 rounded-md border border-gray-200 overflow-hidden",
                hasPartImages
                  ? "group cursor-pointer hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  : "cursor-default"
              )}
            >
              {partThumbUrl ? (
                <>
                  <img
                    src={partThumbUrl}
                    alt={part.title ?? "Part image"}
                    className="w-full h-full object-contain"
                  />
                  {partLightboxImages.length > 1 && (
                    <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      {partLightboxImages.length} photos
                    </span>
                  )}
                </>
              ) : (
                <div className="flex w-full h-full items-center justify-center text-gray-400">
                  <CameraIcon className="h-10 w-10" aria-hidden="true" />
                </div>
              )}
            </button>

            {hasVehicleImage && (
              <button
                type="button"
                onClick={() => setVehicleImageOpen(true)}
                aria-label="View vehicle images"
                className="group relative flex-shrink-0 w-32 h-32 md:w-40 md:h-40 bg-gray-50 rounded-md border border-gray-200 overflow-hidden cursor-pointer hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <img
                  src={vehicleThumbUrl!}
                  alt={vehicleSummary || "Vehicle"}
                  className="w-full h-full object-cover"
                />
                {vehicleLightboxImages.length > 1 && (
                  <span className="absolute top-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                    {vehicleLightboxImages.length} photos
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] font-medium text-white">
                  <TruckIcon className="h-3 w-3" aria-hidden="true" />
                  {vehicleSummary || "Vehicle"}
                </span>
              </button>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 truncate">
                  {part.title ?? "Untitled Part"}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  {vehicleSummary && (
                    <span className="font-medium text-gray-700">
                      {vehicleSummary}
                    </span>
                  )}
                  {part.category?.name && (
                    <span>
                      <span className="text-gray-400">·</span>{" "}
                      <span className="font-medium text-gray-700">
                        {part.category.name}
                      </span>
                    </span>
                  )}
                  {part.vehicle?.vrm && (
                    <span>
                      VRM{" "}
                      <span className="font-medium text-gray-700">
                        {part.vehicle.vrm}
                      </span>
                    </span>
                  )}
                  {part.tagNumber && (
                    <span>
                      Tag{" "}
                      <span className="font-medium text-gray-700">
                        {part.tagNumber}
                      </span>
                    </span>
                  )}
                  {part.partNumber && (
                    <span>
                      Part #{" "}
                      <span className="font-medium text-gray-700">
                        {part.partNumber}
                      </span>
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StockStatusEditor part={part} />
                  <PartGradeBadge part={part} />
                  {daysInStock !== null && (
                    <InfoBadge
                      icon={<ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                      tone="slate"
                      title={`In stock since ${formatDateTime(
                        stockStartIso,
                        DateTime.DATE_MED,
                        "-"
                      )}`}
                    >
                      {daysInStock} {daysInStock === 1 ? "day" : "days"} in stock
                    </InfoBadge>
                  )}
                  {daysListed !== null && (
                    <InfoBadge
                      icon={<ShoppingBagIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                      tone="blue"
                      title={`Listed on ${formatDateTime(
                        listedIso,
                        DateTime.DATE_MED,
                        "-"
                      )}`}
                    >
                      {daysListed} {daysListed === 1 ? "day" : "days"} on eBay
                    </InfoBadge>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                {isSold ? (
                  <>
                    <p className="text-3xl font-semibold text-gray-900">
                      {PriceFormat(soldPrice ?? 0)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sold{" "}
                      {formatDateTime(
                        part.soldDate,
                        DateTime.DATE_MED,
                        ""
                      )}
                    </p>
                  </>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Price
                      </p>
                      <EbayResearchButton part={part} size="xs" />
                    </div>
                    <UnlistedOfferPrice
                      part={part}
                      showButtons={false}
                      hideLabel
                      intelligencePopover
                      compact
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <ButtonInput
                  label="View Listing"
                  isSubmit={false}
                  onClick={() => openInNewTab(`/ebay-listings/${part.id}`)}
                  classes={undefined}
                />

                <Menu as="div" className="relative">
                  <Menu.Button
                    className={classNames(
                      "inline-flex items-center gap-x-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm",
                      "hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    )}
                  >
                    <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                    Actions
                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <MenuHeading>Labels & Shipping</MenuHeading>
                      <ActionItem onClick={printPartLabel}>Print Part Label</ActionItem>
                      <ActionItem
                        onClick={printPartLabelImage}
                        disabled={!labelHtml}
                      >
                        Print Part Label Image
                      </ActionItem>
                      <ActionItem
                        onClick={() => setShippingModalOpen(true)}
                        disabled={!hasOrder}
                      >
                        Manage Shipping Label
                      </ActionItem>
                      <ActionItem
                        onClick={() => setTrackingModalOpen(true)}
                        disabled={isShipped}
                      >
                        Set Tracking Info
                      </ActionItem>

                      <div className="my-1 h-px bg-gray-100" />
                      <MenuHeading>Part</MenuHeading>
                      <ActionItem onClick={() => setQcOpen(true)}>Send to QC</ActionItem>
                      <ActionItem
                        onClick={() => openInNewTab("/vehicles/" + part.vehicle?.id)}
                        disabled={!hasVehicle}
                      >
                        View Vehicle
                      </ActionItem>
                      <ActionItem onClick={handleGoToLead} disabled={!hasVehicle}>
                        View Lead for Vehicle
                      </ActionItem>
                      <ActionItem
                        onClick={() =>
                          openInNewTab(`https://ebay.co.uk/itm/${part.ebayItemNumber}`)
                        }
                        disabled={!hasEbayItem}
                      >
                        View on eBay
                      </ActionItem>

                      {(hasOrder || hasInvoice) && (
                        <div className="my-1 h-px bg-gray-100" />
                      )}
                      {hasOrder && (
                        <ActionItem onClick={() => setCancelOpen(true)}>
                          Cancel Order
                        </ActionItem>
                      )}
                      {hasInvoice && (
                        <>
                          <ActionItem onClick={() => downloadInvoice(invoice!.id!)}>
                            Download Invoice
                          </ActionItem>
                          <ActionItem
                            onClick={() => setCreditModalOpen(true)}
                            danger
                          >
                            Return Part
                          </ActionItem>
                        </>
                      )}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>

            {part.statusMessage && part.partStatus !== PartStatus.Sold && (
              <div className="mt-3 text-sm text-gray-500 italic">
                {part.statusMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

interface ActionItemProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

const ActionItem = ({ children, onClick, disabled, danger }: ActionItemProps) => (
  <Menu.Item disabled={disabled}>
    {({ active }) => (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          active && !disabled ? "bg-gray-100" : "",
          disabled ? "text-gray-400 cursor-not-allowed" : danger ? "text-red-600" : "text-gray-700",
          "w-full px-4 py-2 text-left text-sm"
        )}
      >
        {children}
      </button>
    )}
  </Menu.Item>
);

const MenuHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="px-4 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
    {children}
  </div>
);

const INFO_BADGE_TONES: Record<string, string> = {
  slate: "bg-slate-50 text-slate-700 ring-slate-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
};

const InfoBadge = ({
  children,
  icon,
  tone = "slate",
  title,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: keyof typeof INFO_BADGE_TONES;
  title?: string;
}) => (
  <span
    title={title}
    className={classNames(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
      INFO_BADGE_TONES[tone] ?? INFO_BADGE_TONES.slate
    )}
  >
    {icon}
    {children}
  </span>
);
